import { Layer, Marker, Source } from "@vis.gl/react-maplibre";
import { FC } from "react";

import { MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT } from "@ryde/controls";
import { IAddressSearchBounds } from "@ryde/shared";

import {
  BOUNDS_CORNERS,
  BOUNDS_MAP_COLOR,
  BOUNDS_MAP_FILL_OPACITY,
  BOUNDS_MAP_WEIGHT,
  TBoundsCorner,
} from "./bounds-block.const";
import { BoundsCornerHandleStyled } from "./bounds-block.styled";
import { boundsToGeoJson } from "./bounds-block.utils";

interface IBoundsCropLayerProps {
  bounds: IAddressSearchBounds;
  isEditing: boolean;
  onCornerMove: (args: {
    corner: TBoundsCorner;
    latitude: number;
    longitude: number;
  }) => void;
}

/**
 * The area as a filled rectangle, with a crop handle on each corner while editing.
 *
 * Dragging a handle moves one latitude and one longitude, so the area is always a rectangle —
 * which is the only shape Google's autocomplete accepts. Nothing here can draw, cut or add a
 * vertex, unlike the polygon editor the areas screen uses.
 *
 * `onDrag` fires throughout the gesture, so the numbers beside the map track the handle live
 * rather than catching up when it is released.
 */
export const BoundsCropLayer: FC<IBoundsCropLayerProps> = ({
  bounds,
  isEditing,
  onCornerMove,
}) => (
  <>
    <Source
      id="address-search-bounds"
      type="geojson"
      data={boundsToGeoJson(bounds)}
    >
      <Layer
        id="address-search-bounds-fill"
        type="fill"
        paint={{
          ["fill-color"]: BOUNDS_MAP_COLOR,
          ["fill-opacity"]: BOUNDS_MAP_FILL_OPACITY,
        }}
      />
      <Layer
        id="address-search-bounds-outline"
        type="line"
        paint={{
          ["line-color"]: BOUNDS_MAP_COLOR,
          ["line-width"]: BOUNDS_MAP_WEIGHT,
        }}
      />
    </Source>

    {isEditing &&
      BOUNDS_CORNERS.map(corner => (
        <Marker
          key={corner.key}
          latitude={bounds[corner.latitude]}
          longitude={bounds[corner.longitude]}
          anchor="center"
          draggable
          {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
          onDrag={event =>
            onCornerMove({
              corner,
              latitude: event.lngLat.lat,
              longitude: event.lngLat.lng,
            })
          }
          onDragEnd={event =>
            onCornerMove({
              corner,
              latitude: event.lngLat.lat,
              longitude: event.lngLat.lng,
            })
          }
        >
          <BoundsCornerHandleStyled />
        </Marker>
      ))}
  </>
);
